import React, {useState} from 'react';
import {
    Card,
    Dialog,
    DialogContent,
    DialogTitle, ListItem, Tab, Tabs,
    Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";



// Reusable Step Number Circle Component
const StepNumberCircle = ({ step, color }) => (
    <Box
        sx={{
            position: 'absolute',
            top: '-15px', // Half of the circle's height to center it on the top edge
            left: '50%',
            transform: 'translateX(-50%)',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundColor: color, // Changed this line to use 'color' for background
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
            zIndex: 1, // Ensure it's above the card
            border: `2px solid ${color}`, // Border matching the icon color
        }}
    >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}> {/* Changed text color to white for contrast */}
            {step}
        </Typography>
    </Box>
);


function InfoActivities({ isOpen, onClose }){

    const [tabIndex, setTabIndex] = useState(0);
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };


    // Common Card Styling
    const commonCardSx = {
        borderRadius: '1rem',
        padding: '1.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'transform 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        '&:hover': {
            transform: 'translateY(-5px)',
        },
    };

    // Common Icon Container Styling
    const commonIconContainerSx = {
        padding: '0.75rem',
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    // Responsive Box item styling
    const responsiveBoxItemSx = {
        flexGrow: 1, // Allows items to grow
        flexShrink: 1, // Allows items to shrink
        minWidth: '230px', // Minimum width before wrapping
        maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', lg: 'calc(33.33% - 16px)' }, // Responsive max width
        // For lg:col-span-3 (full width on lg screens)
        '&.full-width-lg': {
            maxWidth: { lg: '100%' },
        },
        // For lg:col-span-8 (2/3 width on lg screens)
        '&.two-thirds-width-lg': {
            maxWidth: { lg: 'calc(66.66% - 16px)' },
        },
    };

    // Styling for the Card component itself when it's a step card
    const stepCardBaseSx = {
        ...commonCardSx, // Inherit base card styles
        borderTop: '2px solid', // Add a top border
        paddingTop: '2.5rem', // Increase padding-top to make space for the circle content
        // Remove individual card hover transform here, as it will be on the parent Box
        '&:hover': {
            boxShadow: '0 8px 15px rgba(0, 0, 0, 0.1)', // Keep shadow change
        },
    };

    // Styling for the Box that wraps both the StepNumberCircle and the Card for numbered steps
    const responsiveBoxItemWithStepSx = {
        ...responsiveBoxItemSx, // Inherit base responsive styles
        position: 'relative', // Crucial for positioning the step number circle
        marginTop: '20px', // Push the entire box down to allow space for the circle above the card's border
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out', // Add transition to the parent box
        '&:hover': {
            transform: 'translateY(-5px)', // Apply hover transform to the parent box
        },
    };



    return (
        <Dialog open={isOpen} onClose={onClose}>
            {/* Dialog Title */}
            <DialogTitle
                sx={{
                    color: '#151269',
                    textAlign: 'center',
                    backgroundColor: 'rgba(198, 226, 255, 0.8)',
                    paddingRight: '30px'
                }}
            >
                Adventure Itinerary: Crafting Your Activities! 🗺️
            </DialogTitle>

            <Typography
                sx={{
                    fontSize: '14px',
                    textAlign: 'center',
                    color: '#151269',
                    backgroundColor: 'rgba(198, 226, 255, 0.8)',
                    padding: '0 20px'
                }}
            >
                Ready to plan the fun? Here's how to easily set up and manage all the exciting activities for your event!
            </Typography>

            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: '#151269',
                })}
            >
                <i className="icon-cross" style={{fontSize: '18px', padding: '5px'}}/>
            </IconButton>


            {/* Sticky Tabs */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    zIndex: 1,
                }}
            >
                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    TabIndicatorProps={{ style: { display: 'none' } }}
                    sx={{
                        '& .MuiTab-root': {
                            fontWeight: 'bold',
                            minHeight: 48,
                            textTransform: 'none',
                            color: '#555',
                        },
                        '& .Mui-selected': {
                            color: '#1976d2',
                        },
                    }}
                >
                    <Tab icon={<i className="icon-map-marker-plus" style={{ fontSize: '18px' }} />} label="Add from Map" value={0} />
                    <Tab icon={<i className="icon-pencil" style={{ fontSize: '18px' }} />} label="Add Manually" value={1} />
                    <Tab icon={<i className="icon-file-pencil" style={{ fontSize: '18px' }} />} label="Modify" value={2} />
                    <Tab icon={<i className="icon-trash" style={{ fontSize: '18px' }} />} label="Delete" value={3} />
                </Tabs>
            </Box>


            <DialogContent sx={{backgroundColor: '#f8fafc' }}>

                {tabIndex === 0 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        {/* NEW: Adding Activities: From the Map! */}
                        <Typography variant="h4" component="h2" sx={{
                            fontSize: '18px',
                            fontWeight: 700, // font-bold
                            color: '#1f2937', // text-gray-800
                            marginBottom: '1.5rem', // mb-6
                            textAlign: 'center',
                        }}>
                            Adding Activities: From the Map! 📍
                        </Typography>
                        <Typography variant="h6" sx={{
                            fontSize: '14px',
                            color: '#4b5563', // text-gray-600
                            marginBottom: '2rem', // mb-8
                            textAlign: 'center',
                        }}>
                            Integrate locations directly from the map into your activity schedule:
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px', // Equivalent to spacing={3}
                            marginBottom: '3rem',
                            justifyContent: 'center'
                        }}>
                            {/* Step 1: Access Map Search */}
                            <Box sx={responsiveBoxItemWithStepSx} >
                                <StepNumberCircle step="1" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-search" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Find Your Spot
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        On the map view (desktop), locate the search box on the right side of the screen.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 2: Select Location Type */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="2" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-store" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Choose Type
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Select the applicable icon in the search box: the left icon for Businesses or the right icon for Residences.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 3: Search & Add */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="3" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-map-marker-plus" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Search & Confirm
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Search for the name or address. Once it appears on the map, click the "Add" button.
                                    </Typography>
                                </Card>
                            </Box>

                            {/* Step 4: Add to Activities */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="4" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-calendar-plus" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Integrate into Itinerary
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Select the "Add to activities" option. The location will then be added to your overall activity list, ready for you to add any additional details like dates, times, and participants!
                                    </Typography>
                                </Card>
                            </Box>


                            {/* Pro Tip Section */}
                            <Box sx={{ ...responsiveBoxItemSx, ...responsiveBoxItemSx['&.full-width-lg'] }}>
                                <Card sx={{
                                    backgroundColor: '#dcfce7', // Light green
                                    borderRadius: '1rem',
                                    padding: '1.5rem',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    marginTop: '1.5rem', // Add some spacing
                                    border: '2px solid #22c55e', // Green border
                                }}>
                                    <i className="icon-lamp-bright" style={{ color: '#166534', fontSize: '2rem' }}/>
                                    <Typography variant="h5" component="h4" sx={{
                                        fontSize: '18px',
                                        fontWeight: 700, // font-bold
                                        color: '#166534', // Green-800
                                        marginBottom: '0.5rem',
                                    }}>
                                        ✨ Pro Tip: Photo Magic! ✨
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                        fontSize: '14px',
                                        color: '#14532d', // Green-900
                                    }}>
                                        Any activity added directly from the map will automatically bring along awesome Google
                                        Photos associated with that location! If you decide to upload your own fantastic photos
                                        later, they'll replace the Google ones.
                                    </Typography>
                                </Card>
                            </Box>
                        </Box>

                    </Box>
                )}

                {tabIndex === 1 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        {/* NEW: Adding Activities: Manually */}
                        <Typography variant="h4" component="h2" sx={{
                            fontSize: '18px',
                            fontWeight: 700, // font-bold
                            color: '#1f2937', // text-gray-800
                            marginBottom: '1.5rem', // mb-6
                            textAlign: 'center',
                        }}>
                            Manual Activity Creation: Your Vision, Your Rules! ✍️
                        </Typography>
                        <Typography variant="h6" sx={{
                            fontSize: '14px',
                            color: '#4b5563', // text-gray-600
                            marginBottom: '2rem', // mb-8
                            textAlign: 'center',
                        }}>
                            When adding an activity manually, you'll specify these key details:
                        </Typography>


                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px', // Equivalent to spacing={3}
                            marginBottom: '3rem',
                            justifyContent: 'center'
                        }}>

                            {/* Step 1: Activity Name */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="1" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-handwriting" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Activity Name
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Give your activity a catchy and descriptive name!
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 2: Dates */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="2" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-calendar3" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Date(s)
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        When's the fun happening? Set a single date or a range of dates for multi-day adventures.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 3: Times */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="3" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-clock" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Times
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Pinpoint the exact hours! You can set a specific time range (e.g., 2:00 PM - 4:00 PM) or mark it as an "All Day" event.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 4: Participants */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="4" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-group" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Assign Your Crew!
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Decide who's joining in! You can select specific attendees from your guest list for each activity.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 5: Location */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="5" color="#0ea5e9" />
                                <Card sx={{...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-map" style={{color: '#0ea5e9', fontSize: '2rem'}}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: '#1f2937',
                                        marginBottom: '0.25rem'
                                    }}>
                                        Location, Location, Location!
                                    </Typography>
                                    <Typography variant="body2" sx={{fontSize: '14px', color: '#4b5563'}}>
                                        Where's the action? Choose from three ways to add a location:
                                    </Typography>
                                    <List sx={{ width: '100%', padding: 0 }}>
                                        <ListItem sx={{ padding: '4px 0' }}>
                                            <ListItemText
                                                primary={<Typography variant="body2" sx={{ fontSize: '12px', color: '#4b5563' }}><strong>Search:</strong> Find establishments or residences by name.</Typography>} />
                                        </ListItem>
                                        <ListItem sx={{ padding: '4px 0' }}>
                                            <ListItemText
                                                primary={<Typography variant="body2" sx={{ fontSize: '12px', color: '#4b5563' }}><strong>Previously Used:</strong> Select from locations you've used in this event before.</Typography>} />
                                        </ListItem>
                                        <ListItem sx={{ padding: '4px 0' }}>
                                            <ListItemText
                                                primary={<Typography variant="body2" sx={{ fontSize: '12px', color: '#4b5563' }}><strong>Manual Entry:</strong> Type in a custom address.</Typography>} />
                                        </ListItem>
                                    </List>
                                </Card>
                            </Box>
                            {/* Step 6: Photos */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="6" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-picture2" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Capture the Moment (Photos)
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Add photos to give everyone a glimpse of the activity! Upload directly from your device.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 7: Notes */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="7" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-notebook" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Important Notes
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Include any special instructions, tips, or extra details participants need to know.
                                    </Typography>
                                </Card>
                            </Box>

                        </Box>
                    </Box>
                )}

                {tabIndex === 2 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        {/* Editing Activities Section */}
                        <Typography variant="h4" component="h2" sx={{
                            fontSize: '18px',
                            fontWeight: 700, // font-bold
                            color: '#1f2937', // text-gray-800
                            marginBottom: '1.5rem', // mb-6
                            textAlign: 'center',
                        }}>
                            Editing Activity Details: Fine-Tuning Your Itinerary! ✏️
                        </Typography>
                        <Typography variant="h6" sx={{
                            fontSize: '14px',
                            color: '#4b5563', // text-gray-600
                            marginBottom: '2rem', // mb-8
                            textAlign: 'center',
                        }}>
                            Whether added manually or from the map, you can always refine your activity details:
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px', // Equivalent to spacing={3}
                            marginBottom: '3rem',
                            justifyContent: 'center'
                        }}>
                            {/* Step 1: Find Your Activity */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="1" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-search" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Find Your Activity
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Locate the activity you wish to edit within your main activity list.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 2: Open for Editing */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="2" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-pencil" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Open for Editing
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Select one of the icons on the left side of the activity's corresponding box to open its editing options.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 3: Make Your Changes */}
                            <Box sx={responsiveBoxItemWithStepSx}>
                                <StepNumberCircle step="3" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height:'300px', borderColor: '#0ea5e9', overflow:'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-check" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Make Your Changes
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        The activity's details will automatically populate the fields below the
                                        activity overview. Make any necessary adjustments to the name, dates, times,
                                        location, photos, notes, cost, or participants.
                                    </Typography>
                                </Card>
                            </Box>

                        </Box>
                        {/* Pro Tip Section */}
                        <Box sx={{ ...responsiveBoxItemSx, ...responsiveBoxItemSx['&.full-width-lg'] }}>
                            <Card sx={{
                                backgroundColor: '#dcfce7', // Light green
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                marginTop: '1.5rem', // Add some spacing
                                border: '2px solid #22c55e', // Green border
                            }}>
                                <i className="icon-lamp-bright" style={{ color: '#166534', fontSize: '2rem' }}/>
                                <Typography variant="h5" component="h4" sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, // font-bold
                                    color: '#166534', // Green-800
                                    marginBottom: '0.5rem',
                                }}>
                                    ✨ Pro Tip: Cost & Splitting! ✨
                                </Typography>
                                <Typography variant="body2" sx={{fontSize: '14px', color: '#4b5563'}}>
                                    Manage expenses for this activity:
                                </Typography>
                                <List sx={{ width: '100%', padding: 0 }}>
                                    <ListItem sx={{ padding: '4px 0 0 0' }}>
                                        <ListItemText
                                            primary={<Typography variant="body2" sx={{ fontSize: '12px', color: '#4b5563' }}><strong>Split Evenly:</strong> Divide the cost equally among all attending
                                                participants.</Typography>} />
                                    </ListItem>
                                    <ListItem sx={{ padding: '4px 0 0 0' }}>
                                        <ListItemText
                                            primary={<Typography variant="body2" sx={{ fontSize: '12px', color: '#4b5563' }}><strong>Custom Split:</strong> Assign specific amounts to each participant.</Typography>} />
                                    </ListItem>
                                    <ListItem sx={{ padding: '4px 0 0 0' }}>
                                        <ListItemText
                                            primary={<Typography variant="body2" sx={{ fontSize: '12px', color: '#4b5563' }}><strong>Business Expense:</strong> Mark the entire cost as a business expense.</Typography>} />
                                    </ListItem>
                                </List>
                            </Card>
                        </Box>
                    </Box>
                )}

                {tabIndex === 3 && (
                    <Box sx={{
                        p: 3,
                        border: '1px solid #e0e0e0',
                        borderRadius: 3,
                        backgroundColor: '#fff',
                    }}>
                        {/* NEW: Deleting Activities */}
                        <Typography variant="h4" component="h2" sx={{
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#1f2937',
                            marginBottom: '1.5rem',
                            textAlign: 'center',
                        }}>
                            Deleting Activities: Clear Your Schedule! 🗑️
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px',
                            marginBottom: '3rem',
                            justifyContent: 'center'
                        }}>
                            {/* Step 1: Access the Delete Option */}
                            <Box sx={{
                                ...responsiveBoxItemWithStepSx,
                                width: { xs: '100%', md: 'calc(50% - 12px)' }
                            }}>
                                <StepNumberCircle step="1" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height: '300px', borderColor: '#0ea5e9', overflow: 'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-trash" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Select "Delete"
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        Locate the activity you wish to delete from your main activity list. Click the **three-dot icon** (⋮) on the right side of the activity's corresponding box and select the **Delete** option.
                                    </Typography>
                                </Card>
                            </Box>
                            {/* Step 2: Confirm Deletion */}
                            <Box sx={{
                                ...responsiveBoxItemWithStepSx,
                                width: { xs: '100%', md: 'calc(50% - 12px)' }
                            }}>
                                <StepNumberCircle step="2" color="#0ea5e9" />
                                <Card sx={{ ...stepCardBaseSx, height: '300px', borderColor: '#0ea5e9', overflow: 'auto' }}>
                                    <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                        <i className="icon-warning" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                                    </Box>
                                    <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                        Confirm Your Choice
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                        A confirmation pop-up will appear to prevent accidental deletions. Simply click the **Delete** button within this pop-up to permanently delete the activity from your schedule.
                                    </Typography>
                                </Card>
                            </Box>
                        </Box>
                    </Box>
                )}

            </DialogContent>
        </Dialog>
    )
}

export default InfoActivities;